import React, { useState, useEffect } from 'react';

const TaskTap = () => {
  const [earnings, setEarnings] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Animate earnings counter
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setEarnings(prev => {
          if (prev >= 77) {
            clearInterval(interval);
            return 77;
          }
          return prev + 1;
        });
      }, 20);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleClaimClick = () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 1000);
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/20 via-black to-blue-900/20" />

        <div className="relative max-w-6xl mx-auto px-4 py-20">
          {/* Logo/Title */}
          <div className="text-center mb-12">
            <h1 className="text-7xl md:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500 mb-4">
              TaskTap
            </h1>
            <p className="text-2xl md:text-3xl text-gray-400">
              Turn Slack into your personal ATM
            </p>
          </div>

          {/* Live Earnings Counter */}
          <div className="text-center mb-16">
            <div className="inline-block">
              <div className="text-6xl md:text-8xl font-bold text-green-400 tabular-nums">
                ${earnings.toFixed(2)}
              </div>
              <div className="text-xl text-gray-500">
                Real money earned this week
              </div>
            </div>
          </div>

          {/* The Brutal Truth */}
          <div className="bg-gray-900 border border-green-400 rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-bold text-green-400 mb-4">
              Here's How It Actually Works
            </h2>
            <div className="space-y-4 text-lg">
              <p>
                <span className="text-yellow-400">1.</span> Task drops in Slack
              </p>
              <p>
                <span className="text-yellow-400">2.</span> First person clicks "Claim"
              </p>
              <p>
                <span className="text-yellow-400">3.</span> Message vanishes instantly (no duplicates)
              </p>
              <p>
                <span className="text-yellow-400">4.</span> DM with task details appears
              </p>
              <p>
                <span className="text-yellow-400">5.</span> Complete task, get paid immediately
              </p>
              <p className="text-2xl text-green-400 font-bold mt-6">
                That's it. No BS.
              </p>
            </div>
          </div>

          {/* Simulated Slack Message */}
          <div className="bg-gray-900 rounded-lg p-6 mb-12 border border-gray-700">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-green-400 rounded flex items-center justify-center text-black font-bold">
                T
              </div>
              <div className="flex-1">
                <div className="flex items-baseline space-x-2 mb-2">
                  <span className="font-bold text-white">TaskTap</span>
                  <span className="text-xs text-gray-500">BOT</span>
                  <span className="text-xs text-gray-500">2:34 PM</span>
                </div>
                <div className="bg-gray-800 rounded p-4 border border-gray-700">
                  <div className="mb-3">
                    <span className="text-yellow-400 font-bold">📋 New Task Available!</span>
                  </div>
                  <div className="text-gray-300 mb-3">
                    Review and categorize 50 customer feedback entries
                  </div>
                  <div className="text-green-400 font-bold mb-4">
                    💰 Reward: $25.00
                  </div>
                  <button
                    onClick={handleClaimClick}
                    className={`px-6 py-2 rounded font-bold transition-all ${
                      isAnimating
                        ? 'bg-red-500 text-white animate-pulse'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                  >
                    {isAnimating ? '❌ CLAIMED!' : '⚡ Claim Task'}
                  </button>
                  {isAnimating && (
                    <div className="mt-3 text-red-400 animate-fade-in">
                      Message deleted. Task assigned to @you
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* The Numbers Don't Lie */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gray-900 border border-green-400 rounded-lg p-6 text-center">
              <div className="text-4xl font-bold text-green-400">$77</div>
              <div className="text-gray-400">Already Earned</div>
            </div>
            <div className="bg-gray-900 border border-blue-400 rounded-lg p-6 text-center">
              <div className="text-4xl font-bold text-blue-400">&lt; 500ms</div>
              <div className="text-gray-400">Claim Speed</div>
            </div>
            <div className="bg-gray-900 border border-yellow-400 rounded-lg p-6 text-center">
              <div className="text-4xl font-bold text-yellow-400">100%</div>
              <div className="text-gray-400">Approval Rate</div>
            </div>
          </div>

          {/* Why This Matters */}
          <div className="bg-gradient-to-r from-green-900/50 to-blue-900/50 rounded-lg p-8 mb-12 border border-green-400">
            <h2 className="text-3xl font-bold text-white mb-4">
              Why TaskTap Changes Everything
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-bold text-yellow-400 mb-2">
                  Old Way 💩
                </h3>
                <ul className="space-y-2 text-gray-400">
                  <li>• Apply for gigs</li>
                  <li>• Wait for approval</li>
                  <li>• Complete work</li>
                  <li>• Invoice</li>
                  <li>• Wait 30+ days for payment</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-green-400 mb-2">
                  TaskTap Way 🚀
                </h3>
                <ul className="space-y-2 text-white">
                  <li>• See task</li>
                  <li>• Click button</li>
                  <li>• Do work</li>
                  <li>• Get paid instantly</li>
                  <li>• Repeat</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Security Note */}
          <div className="bg-black border border-gray-700 rounded-lg p-6 mb-12">
            <h3 className="text-xl font-bold text-yellow-400 mb-2">
              🔒 How We Keep It Secure
            </h3>
            <p className="text-gray-400">
              Dashboard URLs use SHA-256 hashes. Only accessible through Slack DMs.
              No passwords, no logins, no BS. Your earnings are tracked, secured, and
              only you can see them.
            </p>
            <div className="mt-4 font-mono text-sm text-green-400">
              /tasktap/dashboard/{'{userId}'}/{'{sha256Hash}'}
            </div>
          </div>

          {/* The Philosophy */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500 mb-4">
              The Philosophy
            </h2>
            <div className="text-xl text-gray-300 space-y-2">
              <p>Work should be instant.</p>
              <p>Payment should be instant.</p>
              <p>Everything else is friction.</p>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mb-12">
            <div className="inline-block">
              <div className="text-3xl font-bold text-yellow-400 mb-4">
                Ready to turn Slack into cash?
              </div>
              <div className="bg-green-500 text-black px-8 py-4 rounded-lg font-bold text-xl inline-block">
                Coming Soon™
              </div>
              <div className="text-gray-500 mt-4">
                (But actually works - we already earned $77)
              </div>
            </div>
          </div>

          {/* Footer Stats */}
          <div className="border-t border-gray-800 pt-8">
            <div className="text-center text-gray-500">
              <p>Built in one session. $77 earned. 3 tasks completed.</p>
              <p className="text-green-400 mt-2">
                This isn't a concept. It's running in production.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Floating indicators */}
      <div className="fixed bottom-4 right-4 space-y-2">
        <div className="bg-green-500 text-black px-3 py-1 rounded-full text-sm font-bold animate-pulse">
          LIVE
        </div>
        <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-bold">
          EARNING
        </div>
      </div>
    </div>
  );
};

export default TaskTap;